import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Box,
  Chip,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CurrencyRupee as MoneyIcon,
  CalendarToday as CalendarIcon,
  LocalLibrary as LibraryIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL;

const MonthlyFee = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Only library fees now
  const [libraryFees, setLibraryFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [form, setForm] = useState({
    monthly_fee: "",
    deposit_amount: "",
    effective_from: "",
  });

  const token = localStorage.getItem("token");
  const config = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchAllFees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllFees = async () => {
    setLoading(true);
    try {
      const [libraryResponse] = await Promise.all([
        axios.get(`${API}library/monthly-fee/`, config),
      ]);
      setLibraryFees(libraryResponse.data);
    } catch (err) {
      toast.error("Failed to fetch monthly fees");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditItem(item);
      setForm({
        monthly_fee: item.monthly_fee,
        deposit_amount: item.deposit_amount,
        effective_from: item.effective_from,
      });
    } else {
      setEditItem(null);
      setForm({
        monthly_fee: "",
        deposit_amount: "",
        effective_from: "",
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditItem(null);
    setForm({
      monthly_fee: "",
      deposit_amount: "",
      effective_from: "",
    });
  };

  const handleSubmit = async () => {
    if (!form.monthly_fee || !form.deposit_amount || !form.effective_from) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      if (editItem) {
        await axios.patch(
          `${API}library/monthly-fee/${editItem.id}/`,
          form,
          config
        );
        toast.success("Monthly fee updated successfully");
      } else {
        await axios.post(`${API}library/monthly-fee/`, form, config);
        toast.success("Monthly fee added successfully");
      }
      fetchAllFees();
      handleCloseModal();
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.monthly_fee?.[0] ||
        err.response?.data?.deposit_amount?.[0] ||
        err.response?.data?.effective_from?.[0] ||
        "Failed to save monthly fee";
      toast.error(errorMessage);
    }
  };

  const handleDelete = (item) => {
    setConfirmModal({
      open: true,
      title: "Delete Monthly Fee",
      message: `Are you sure you want to delete the monthly fee of ₹${item.monthly_fee}? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await axios.delete(
            `${API}library/monthly-fee/${item.id}/`,
            config
          );
          toast.success("Monthly fee deleted successfully");
          fetchAllFees();
        } catch (err) {
          toast.error("Failed to delete monthly fee");
        } finally {
          setConfirmModal({
            open: false,
            title: "",
            message: "",
            onConfirm: null,
          });
        }
      },
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateTimeString) => {
    return new Date(dateTimeString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const currentFees = libraryFees;
  const feeType = "Library";

  const isCurrentFee = (effectiveFrom) => {
    const today = new Date();
    const effectiveDate = new Date(effectiveFrom);

    // If there's only one fee, it's automatically current
    if (currentFees.length === 1) {
      return true;
    }

    // Otherwise, check if the effective date is today or in the past
    return effectiveDate <= today;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6">
      <div className="max-w-full mx-auto">
        {/* Tabs Section (single Library tab to keep layout same) */}
        <div className="bg-white rounded-2xl shadow-lg mb-6 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <Box
              sx={{ borderBottom: 1, borderColor: "divider" }}
              className="flex-1"
            >
              <Tabs
                value={0}
                onChange={() => {}}
                aria-label="fee type tabs"
                variant={isMobile ? "fullWidth" : "standard"}
                sx={{
                  "& .MuiTab-root": {
                    minHeight: isMobile ? "64px" : "48px",
                    fontSize: isMobile ? "0.875rem" : "1rem",
                    fontWeight: 600,
                  },
                  "& .Mui-selected": {
                    color: "#3b82f6 !important",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#3b82f6",
                    height: "3px",
                  },
                }}
              >
                <Tab
                  icon={<LibraryIcon />}
                  label={isMobile ? "Library" : "Library Fees"}
                  className="flex-1"
                />
              </Tabs>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg ml-4"
              size={isSmallMobile ? "small" : "medium"}
            >
              {isSmallMobile ? "Add Fee" : `Add ${feeType} Fee`}
            </Button>
          </div>
        </div>

        {/* Content Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="relative">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <MoneyIcon className="text-blue-600 text-xl" />
                  </div>
                </div>
                <Typography
                  variant="h6"
                  className="text-gray-700 font-semibold mb-2"
                >
                  Loading monthly fees...
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Please wait while we fetch the latest data
                </Typography>
              </div>
            </div>
          ) : currentFees.length === 0 ? (
            <div className="text-center py-16">
              <div className="mb-6">
                <div className="w-24 h-24 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MoneyIcon className="text-blue-600 text-3xl" />
                </div>
                <Typography
                  variant="h5"
                  className="text-gray-700 font-semibold mb-2"
                >
                  No {feeType.toLowerCase()} monthly fees configured
                </Typography>
                <Typography variant="body1" className="text-gray-500 mb-6">
                  Add your first {feeType.toLowerCase()} monthly fee to get
                  started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenModal()}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  Add First {feeType} Fee
                </Button>
              </div>
            </div>
          ) : (
            <div
              className={`grid gap-6 ${
                isSmallMobile
                  ? "grid-cols-1"
                  : isMobile
                  ? "grid-cols-1 md:grid-cols-2"
                  : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              }`}
            >
              {currentFees.map((fee) => (
                <Card
                  key={fee.id}
                  className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
                  sx={{
                    "&:hover": {
                      borderColor: "#3b82f6",
                    },
                  }}
                >
                  <CardHeader
                    avatar={
                      <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg">
                        <MoneyIcon className="text-white" />
                      </div>
                    }
                    action={
                      <div className="flex gap-1">
                        <IconButton
                          onClick={() => handleOpenModal(fee)}
                          size="small"
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(fee)}
                          size="small"
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    }
                    title={
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-bold text-gray-800">
                          ₹{fee.monthly_fee}
                        </span>
                        {isCurrentFee(fee.effective_from) && (
                          <Chip
                            label="Current"
                            color="success"
                            size="small"
                            className="text-xs font-semibold"
                            sx={{
                              backgroundColor: "#10b981",
                              color: "white",
                            }}
                          />
                        )}
                      </div>
                    }
                    subheader={
                      <div className="flex items-center gap-2 text-gray-600">
                        <CalendarIcon fontSize="small" />
                        <span>
                          Effective from {formatDate(fee.effective_from)}
                        </span>
                      </div>
                    }
                    className="bg-gradient-to-r from-blue-50 to-indigo-50"
                    sx={{
                      "& .MuiCardHeader-content": {
                        minWidth: 0,
                      },
                    }}
                  />
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <Typography
                          variant="body2"
                          className="text-gray-600 font-medium"
                        >
                          Monthly Fee:
                        </Typography>
                        <Typography
                          variant="body1"
                          className="font-bold text-gray-800"
                        >
                          ₹{fee.monthly_fee}
                        </Typography>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <Typography
                          variant="body2"
                          className="text-gray-600 font-medium"
                        >
                          Deposit Amount:
                        </Typography>
                        <Typography
                          variant="body1"
                          className="font-bold text-gray-800"
                        >
                          ₹{fee.deposit_amount}
                        </Typography>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                        <Typography
                          variant="body2"
                          className="text-gray-700 font-semibold"
                        >
                          Total Amount:
                        </Typography>
                        <Typography
                          variant="h6"
                          className="font-bold text-green-700"
                        >
                          ₹
                          {(
                            parseFloat(fee.monthly_fee) +
                            parseFloat(fee.deposit_amount)
                          ).toFixed(2)}
                        </Typography>
                      </div>

                      <div className="pt-3 border-t border-gray-200">
                        <Typography
                          variant="caption"
                          className="text-gray-500 flex items-center gap-1"
                        >
                          <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                          Last updated: {formatDateTime(fee.updated_at)}
                        </Typography>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth={isSmallMobile ? "xs" : "sm"}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            margin: isSmallMobile ? "16px" : "32px",
          },
        }}
      >
        <DialogTitle className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <MoneyIcon className="text-white" />
            </div>
            <Typography variant="h6" className="font-bold">
              {editItem
                ? `Edit ${feeType} Monthly Fee`
                : `Add New ${feeType} Monthly Fee`}
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent className="p-6">
          <div className="space-y-6 pt-4">
            <TextField
              fullWidth
              label="Monthly Fee (₹)"
              type="number"
              value={form.monthly_fee}
              onChange={(e) =>
                setForm({ ...form, monthly_fee: e.target.value })
              }
              required
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Enter the monthly fee amount"
              variant="outlined"
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />

            <TextField
              fullWidth
              label="Deposit Amount (₹)"
              type="number"
              value={form.deposit_amount}
              onChange={(e) =>
                setForm({ ...form, deposit_amount: e.target.value })
              }
              required
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Enter the deposit amount"
              variant="outlined"
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />

            <TextField
              fullWidth
              label="Effective From"
              type="date"
              value={form.effective_from}
              onChange={(e) =>
                setForm({ ...form, effective_from: e.target.value })
              }
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: new Date().toISOString().split("T")[0],
              }}
              helperText="Select the date from which this fee will be effective"
              variant="outlined"
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
          </div>
        </DialogContent>
        <DialogActions className="p-6 pt-0 gap-3">
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            fullWidth={isSmallMobile}
            sx={{
              borderRadius: "12px",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            fullWidth={isSmallMobile}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            sx={{
              borderRadius: "12px",
            }}
          >
            {editItem ? "Update" : "Add"} {feeType} Fee
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmModal.open}
        onClose={() =>
          setConfirmModal({
            open: false,
            title: "",
            message: "",
            onConfirm: null,
          })
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            margin: isSmallMobile ? "16px" : "32px",
          },
        }}
      >
        <DialogTitle className="flex items-center gap-3 text-red-600 p-6">
          <div className="p-2 bg-red-100 rounded-lg">
            <DeleteIcon color="error" />
          </div>
          <Typography variant="h6" className="font-bold">
            {confirmModal.title}
          </Typography>
        </DialogTitle>
        <DialogContent className="p-6 pt-0">
          <Typography variant="body1" className="text-gray-700 leading-relaxed">
            {confirmModal.message}
          </Typography>
        </DialogContent>
        <DialogActions className="p-6 pt-0 gap-3">
          <Button
            onClick={() =>
              setConfirmModal({
                open: false,
                title: "",
                message: "",
                onConfirm: null,
              })
            }
            variant="outlined"
            fullWidth={isSmallMobile}
            sx={{
              borderRadius: "12px",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmModal.onConfirm}
            variant="contained"
            color="error"
            fullWidth={isSmallMobile}
            className="bg-red-600 hover:bg-red-700"
            sx={{
              borderRadius: "12px",
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MonthlyFee;
